import { useAuth } from '@/src/context/AuthContext';
import { useTheme } from '@/src/context/ThemeContext';
import { useSubscription } from '@/src/context/SubscriptionContext';
import { createLeagueUseCase } from '@/src/core/dependencies';
import { ModerationError } from '@/src/domain/models/Moderation';
import { useTranslation } from '@/src/i18n';
import { CreateLeagueScreen } from '@/src/presentation/screens/CreateLeagueScreen';
import { useImageUpload } from '@/src/presentation/hooks/useImageUpload';
import { platformAlert } from '@/src/core/utils/platformAlert';
import { logger } from '@/src/core/utils';
import { Stack, useRouter } from 'expo-router';
import { useState } from 'react';

const POINT_PRESETS: Record<string, number[]> = {
  f1: [25, 18, 15, 12, 10, 8, 6, 4, 2, 1],
  linear: [10, 8, 6, 5, 4, 3, 2, 1],
  winner: [3, 1],
};

export default function CreateLeagueContainer() {
  const router = useRouter();
  const { colors } = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { checkAccess } = useSubscription();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [selectedPreset, setSelectedPreset] = useState('f1');
  const [customPoints, setCustomPoints] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [creating, setCreating] = useState(false);

  const { pickAndUploadImage: pickLogo, uploading: uploadingLogo } = useImageUpload({
    bucket: 'league-logos',
    userId: user?.id || '',
    preset: 'avatar',
    fieldType: 'avatar_image',
    onSuccess: setLogoUrl,
  });

  const handleCreate = async () => {
    if (!user) return;
    if (!name.trim()) {
      platformAlert(t('common.error'), t('leagues.nameRequired'));
      return;
    }

    // Feature gating: check league limit
    const leagueAccess = await checkAccess({ feature: 'create_league' });
    if (!leagueAccess.allowed) {
      platformAlert(t('subscription.featureLocked'), t('subscription.leagueLimitReached'), [
        { text: t('common.cancel'), style: 'cancel' },
        { text: t('subscription.upgradeToPro'), onPress: () => router.push('/paywall') },
      ]);
      return;
    }

    try {
      setCreating(true);

      let pointSystem: number[];
      if (selectedPreset === 'custom') {
        pointSystem = customPoints
          .split(',')
          .map((s) => parseInt(s.trim(), 10))
          .filter((n) => !isNaN(n) && n > 0);

        if (pointSystem.length === 0) {
          platformAlert(t('common.error'), t('leagues.invalidPointSystem'));
          return;
        }
      } else {
        pointSystem = POINT_PRESETS[selectedPreset];
      }

      const league = await createLeagueUseCase.execute({
        name: name.trim(),
        description: description.trim() || undefined,
        logoUrl: logoUrl || undefined,
        pointSystem,
        isPublic,
        userId: user.id,
      });

      platformAlert(t('common.success'), t('leagues.leagueCreated'));
      router.replace(`/league/${league.id}`);
    } catch (error) {
      if (error instanceof ModerationError) {
        platformAlert(t('common.error'), t('moderation.contentBlocked'));
      } else {
        logger.error('CreateLeague', 'Failed to create league', { error });
        platformAlert(t('common.error'), error instanceof Error ? error.message : t('leagues.failedToCreate'));
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    router.back();
  };

  return (
    <>
      <Stack.Screen options={{ title: t('leagues.createLeague') }} />
      <CreateLeagueScreen
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        logoUrl={logoUrl}
        pickLogo={pickLogo}
        uploadingLogo={uploadingLogo}
        selectedPreset={selectedPreset}
        setSelectedPreset={setSelectedPreset}
        customPoints={customPoints}
        setCustomPoints={setCustomPoints}
        isPublic={isPublic}
        setIsPublic={setIsPublic}
        creating={creating}
        handleCreate={handleCreate}
        handleCancel={handleCancel}
        colors={colors}
      />
    </>
  );
}
